import React, { useState } from "react";
import PropTypes from "prop-types";
import { Swiper, SwiperSlide } from "swiper/react";
import {
  Autoplay,
  Navigation,
  Pagination,
  Mousewheel,
  Keyboard,
} from "swiper/modules";
import "swiper/css";
import Card from "../instances/Card";
import SectionNumber from "../global/SectionNumber";
import SmartColour from "../../utils/ColourHandler";
import SmartText from "../../utils/TextHandler";
import SmartStyles from "../../utils/StyleHandler";
import Progress from "../global/Progress";

/********************************
<Carousel />
/********************************
Version: 1
Created: 29 01 2025
Updated: 19 02 2025
Author: Richy Pears
/********************************
A carousel / swiper controlled by Storyblok
/********************************/

const Carousel = ({ blok }) => {
  const [activeIndex, setActiveIndex] = useState(1);

  if (!blok) return null; // Escape hatch
  const {
    autoplay,
    copy,
    items,
    number,
    padding,
    rounded,
    subtitle,
    theme,
    title,
  } = blok;

  const swiperAutoplaySettings = {
    delay: autoplay !== "false" && autoplay,
    disableOnInteraction: true,
    pauseOnMouseEnter: true,
    //    stopOnLastSlide: true,
  };

  function getSwiperAutoplaySettings(auto) {
    if (auto === "false") return false;

    return {
      delay: auto,
      disableOnInteraction: true,
      pauseOnMouseEnter: true,
      //    stopOnLastSlide: true,
    };
  }

  const colour = SmartColour(theme);

  const randomId = "brand_controller_" + Math.floor(Math.random() * 100) + 1; // Random string created for unique ID

  return (
    <section
      className={`flex flex-col ${
        colour.classes && colour.classes
      } ${SmartStyles("padding", padding)} ${SmartStyles("rounded", rounded)}`}
      id={randomId}
    >
      <div className="brand-container flex flex-col gap-10 md:gap-12 mb-10 md:mb-12">
        <SectionNumber title={subtitle && subtitle} number={number && number} />
        {copy?.content[0]?.content && (
          <SmartText className="m-0 text-center brand-remove-last-margin">
            {copy}
          </SmartText>
        )}
      </div>
      <div className="w-full h-full brand-controller-wrapper mb-10 md:mb-12">
        <Swiper
          className="3xl:brand-feathered h-full"
          autoHeight={false}
          autoplay={
            autoplay === "false"
              ? false
              : {
                  delay: autoplay,
                  disableOnInteraction: true,
                  pauseOnMouseEnter: true,
                }
          }
          centeredSlides={false}
          grabCursor={true}
          loop={false}
          modules={[Autoplay, Navigation, Pagination, Mousewheel, Keyboard]}
          navigation={{
            prevEl: `#${randomId} .brand-controller-button-previous`,
            nextEl: `#${randomId} .brand-controller-button-next`,
          }}
          slidesPerView="auto"
          initialSlide={0}
          breakpoints={{
            1024: {
              centeredSlides: true,
              initialSlide: 1,
            },
          }}
          onSlideChange={(swiper) => setActiveIndex(swiper.activeIndex + 1)}
        >
          {items &&
            items.map((node, index) => {
              return (
                <SwiperSlide className="max-w-[290px] md:max-w-[320px] lg:max-w-[360px] min-h-full">
                  <Card
                    columns={{ lg: "1", md: "1", sm: "1", xl: "1", xs: "1" }}
                    node={node && node}
                    number={index + 1}
                    theme={colour.text && colour.text}
                  />
                </SwiperSlide>
              );
            })}
        </Swiper>
      </div>
      <div className="brand-container flex flex-row justify-between md:justify-end items-center gap-5">
        <span className="brand-button aspect-square w-12 p-0 brand-controller-button-previous order-1">
          <span className="material-icons text-4xl brand-icon-start">
            chevron_left
          </span>
        </span>
        <span className="brand-button aspect-square w-12 p-0 brand-controller-button-next order-3 md:order-2">
          <span className="material-icons text-4xl">chevron_right</span>
        </span>
        <Progress
          current={activeIndex && activeIndex}
          total={items && items.length}
          css="font-medium tracking-[4px] uppercase m-0 text-xs md:text-base order-2 md:order-3"
        />
      </div>
    </section>
  );
};

export default Carousel;

Carousel.defaultProps = {
  blok: null,
};

Carousel.propTypes = {
  blok: PropTypes.object,
};
